import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import cls from "./UserMemberPartnerSingle.module.scss";
import api from "@/utils/axios";
import { timeAgo } from "@/utils/timeAgo";

const UserMemberPartnerSingle = () => {
  const navigate = useNavigate();
  const { userId, partnerId } = useParams();

  const [partner, setPartner] = useState(null);
  const [memberId, setMemberId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadPartner = async () => {
    if (!userId || !partnerId) return;
    try {
      setLoading(true);
      setError("");
      const res = await api.get(
        `/v1/admin/members/single/partners-info/single/${userId}/${partnerId}`
      );
      setMemberId(res.data?.memberId || null);
      setPartner(res.data?.partner || null);
    } catch (err) {
      console.error("Error loading partner:", err);
      setError("Failed to load partner details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPartner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, partnerId]);

  const handleBack = () => {
    navigate(`/admin/members/single/partners-info/${userId}`);
  };

  const handleGoPersonal = () => {
    navigate(
      `/admin/members/single/partners-info/personal/${userId}/${partnerId}`
    );
  };
  const handleGoRelation = () => {
    navigate(
      `/admin/members/single/partners-info/relation/${userId}/${partnerId}`
    );
  };

  const handleGoLifestyle = () => {
    navigate(
      `/admin/members/single/partners-info/lifestyle/${userId}/${partnerId}`
    );
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  };

  const formatLabel = (v) => {
    if (!v) return "";
    return v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const computed = useMemo(() => {
    if (!partner) {
      return {
        fullName: "",
        genderLabel: "",
        relationshipTypeLabel: "",
        relationshipStatusLabel: "",
        hasRealImage: false,
        imageUrl: null,
      };
    }

    const pp = partner.personal || {};
    const fullName = [pp.firstName, pp.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();

    const genderLabel = pp.gender ? formatLabel(pp.gender) : "—";
    const relationshipTypeLabel = partner.relationshipType
      ? formatLabel(partner.relationshipType)
      : "Not set";
    const relationshipStatusLabel = partner.relationshipStatus
      ? formatLabel(partner.relationshipStatus)
      : "Not set";

    const hasRealImage = !!pp.realImage?.url;
    const imageUrl = pp.realImage?.url || null;

    return {
      fullName: fullName || "Unnamed Partner",
      genderLabel,
      relationshipTypeLabel,
      relationshipStatusLabel,
      hasRealImage,
      imageUrl,
    };
  }, [partner]);

  if (loading) {
    return (
      <div className={cls.page}>
        <div className={cls.headerRow}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back to Partners
          </button>
          <div className={cls.titleBlock}>
            <h1 className={cls.title}>Partner Profile</h1>
            <p className={cls.subtitle}>Loading partner details…</p>
          </div>
        </div>

        <div className={cls.loadingCard}>
          <span className={cls.spinner} />
          <span>Fetching partner from the Universe…</span>
        </div>
      </div>
    );
  }

  if (error || !partner) {
    return (
      <div className={cls.page}>
        <div className={cls.headerRow}>
          <button type="button" className={cls.backBtn} onClick={handleBack}>
            ← Back to Partners
          </button>
          <div className={cls.titleBlock}>
            <h1 className={cls.title}>Partner Profile</h1>
            <p className={cls.subtitle}>Something went wrong.</p>
          </div>
        </div>

        <div className={cls.errorCard}>
          <p>{error || "Partner not found."}</p>
        </div>
      </div>
    );
  }

  const {
    fullName,
    genderLabel,
    relationshipTypeLabel,
    relationshipStatusLabel,
    hasRealImage,
    imageUrl,
  } = computed;

  const isPrimary = !!partner.isPrimary;
  const isDeleted = !!partner.isDeleted;

  return (
    <div className={cls.page}>
      {/* Header */}
      <div className={cls.headerRow}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          ← Back to Partners
        </button>

        <div className={cls.titleBlock}>
          <h1 className={cls.title}>Partner Profile</h1>
          <p className={cls.subtitle}>
            Full snapshot of this partner’s connection with the member, with
            real image, relationship details, and audit info.
          </p>
        </div>
      </div>

      {/* Meta strip */}
      <section className={cls.metaStrip}>
        <div className={cls.metaLeft}>
          <div className={cls.metaLabel}>Link Context</div>
          <div className={cls.metaValues}>
            <span className={cls.metaChip}>
              User ID:
              <span className={cls.metaChipValue}>{userId}</span>
            </span>
            {memberId && (
              <span className={cls.metaChip}>
                Member ID:
                <span className={cls.metaChipValue}>{memberId}</span>
              </span>
            )}
            <span className={cls.metaChip}>
              Partner ID:
              <span className={cls.metaChipValue}>{partner.id}</span>
            </span>
          </div>
        </div>
        <div className={cls.metaRight}>
          <div className={cls.flagRow}>
            {isPrimary && <span className={cls.flagPrimary}>Primary</span>}
            {isDeleted && <span className={cls.flagDeleted}>Deleted</span>}
          </div>
        </div>
      </section>

      {/* Hero card */}
      <section className={cls.heroCard}>
        <div className={cls.heroMedia}>
          <div
            className={
              hasRealImage ? cls.heroAvatarImage : cls.heroAvatarFallback
            }
            style={
              hasRealImage ? { backgroundImage: `url(${imageUrl})` } : undefined
            }
          >
            {!hasRealImage && (
              <span className={cls.heroInitial}>
                {fullName ? fullName.charAt(0).toUpperCase() : "P"}
              </span>
            )}
          </div>

          {hasRealImage && (
            <button
              type="button"
              className={cls.viewImageBtn}
              onClick={() => {
                if (imageUrl) {
                  window.open(imageUrl, "_blank", "noopener,noreferrer");
                }
              }}
            >
              View Image →
            </button>
          )}
        </div>

        <div className={cls.heroBody}>
          <div className={cls.heroHeaderRow}>
            <h2 className={cls.heroName}>{fullName}</h2>
            <span className={cls.partnerGender}>{genderLabel}</span>
          </div>

          <div className={cls.heroTagsRow}>
            <div className={cls.heroTagGroup}>
              <span className={cls.heroTagLabel}>Relationship Type</span>
              <span className={cls.partnerMetaValue}>
                {relationshipTypeLabel}
              </span>
            </div>
            <div className={cls.heroTagGroup}>
              <span className={cls.heroTagLabel}>Relationship Status</span>
              <span className={cls.partnerMetaValue}>
                {relationshipStatusLabel}
              </span>
            </div>
          </div>

          <div className={cls.heroFlagsRow}>
            <span className={cls.flagChip}>
              Primary:{" "}
              <strong>{isPrimary ? "Yes (Highlighted partner)" : "No"}</strong>
            </span>
            <span className={cls.flagChip}>
              Deleted: <strong>{isDeleted ? "Yes" : "No"}</strong>
            </span>
          </div>

          <div className={cls.heroDatesRow}>
            <div className={cls.heroDateItem}>
              <span className={cls.heroDateLabel}>Created</span>
              <span className={cls.heroDateValue}>
                {formatDate(partner.createdAt)}
                {partner.createdAt && (
                  <span className={cls.heroMuted}>
                    {" "}
                    ({timeAgo(partner.createdAt)})
                  </span>
                )}
              </span>
            </div>
            <div className={cls.heroDateItem}>
              <span className={cls.heroDateLabel}>Last Updated</span>
              <span className={cls.heroDateValue}>
                {formatDate(partner.updatedAt)}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Image + keys */}
      <section className={cls.section}>
        <div className={cls.sectionHeader}>
          <h2 className={cls.sectionTitle}>Real Image & Storage Key</h2>
          <p className={cls.sectionSubtitle}>
            Admin-only view of the private real image associated with this
            partner and its S3/CDN key.
          </p>
        </div>

        <div className={cls.imageCard}>
          <div className={cls.imagePreviewWrap}>
            {hasRealImage ? (
              <div
                className={cls.imagePreview}
                style={{ backgroundImage: `url(${imageUrl})` }}
              />
            ) : (
              <div className={cls.imagePreviewFallback}>
                <span>No real image uploaded</span>
              </div>
            )}
          </div>

          <div className={cls.imageMeta}>
            <div className={cls.imageMetaRow}>
              <span className={cls.imageMetaLabel}>Real Image Key</span>
              <span className={cls.imageMetaValue}>
                {partner.personal?.realImage?.key || "—"}
              </span>
            </div>
            <div className={cls.imageMetaRow}>
              <span className={cls.imageMetaLabel}>Real Image Updated At</span>
              <span className={cls.imageMetaValue}>
                {partner.personal?.realImage?.updatedAt
                  ? formatDate(partner.personal.realImage.updatedAt)
                  : "—"}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation cards */}
      <section className={cls.section}>
        <div className={cls.sectionHeader}>
          <h2 className={cls.sectionTitle}>Deep Dive</h2>
          <p className={cls.sectionSubtitle}>
            Jump into detailed Partner Personal and Lifestyle information pages.
          </p>
        </div>

        <div className={cls.linkGrid}>
          <button
            type="button"
            className={`${cls.linkCard} ${cls.cardPersonal}`}
            onClick={handleGoPersonal}
          >
            <div className={cls.linkCardTitle}>Partner Personal Info</div>
            <p className={cls.linkCardText}>
              See private personal details for this partner (age, location and
              other onboarding fields).
            </p>
          </button>

          <button
            type="button"
            className={`${cls.linkCard} ${cls.cardLifestyle}`}
            onClick={handleGoLifestyle}
          >
            <div className={cls.linkCardTitle}>Partner Lifestyle Info</div>
            <p className={cls.linkCardText}>
              Review this partner’s lifestyle, preferences, boundaries and
              interests.
            </p>
          </button>
          <button
            type="button"
            className={`${cls.linkCard} ${cls.cardRelation}`}
            onClick={handleGoRelation}
          >
            <div className={cls.linkCardTitle}>Relation Info</div>
            <p className={cls.linkCardText}>
              View full relationship metadata including type, status, boundaries
              and connection notes.
            </p>
          </button>
        </div>
      </section>
    </div>
  );
};

export default UserMemberPartnerSingle;
